// Wheel › Prizes: what is on the dial, how likely each one is, and how many are
// left (156, decision C10).
//
// THE SLICES STAY SETUP'S. This page gives each slice a kind, a weight and,
// for a prize, stock; adding, removing and renaming slices is Setup's, so the
// dial is edited in one place. The service picks the segment before the wheel
// moves, so the chances drawn here are the odds a visitor actually gets: a
// prize with nothing left is never landed on, and its weight drops out.
//
// Advertised to an Admin and an Editor for a client they hold (roleAccess.ts,
// `wheelPrizes`). The service refuses anybody else in its own words.

import { NoticeBox, PageHead, Seg2, Setting, StickyBar, Switch } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { readPrizes, savePrizes, type GoodUntil, type PrizeDesk, type SegmentKind } from "../api/wheel.js";
import type { ApiOptions, Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { Refusal } from "./Refusal.js";
import { useShow } from "./Show.js";

export type WheelPrizesProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly onSetup: () => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type Show =
  | { readonly phase: "reading" }
  | { readonly phase: "ready"; readonly desk: PrizeDesk }
  | { readonly phase: "failed"; readonly failure: Failure };

/** One row of the dial as it is being edited: numbers as typed. */
type Row = {
  readonly sliceId: string;
  readonly label: string;
  readonly kind: SegmentKind;
  readonly weight: string;
  /** Empty is unlimited. */
  readonly stock: string;
  readonly given: number;
  /** True for a slice Setup added since the prizes were saved. */
  readonly fresh: boolean;
};

const GOOD_UNTIL_LABEL: Readonly<Record<GoodUntil, string>> = {
  show_closes: "The show closes",
  "7_days": "7 days",
  "30_days": "30 days",
};
const GOOD_UNTIL_OF = Object.fromEntries(
  (Object.entries(GOOD_UNTIL_LABEL) as [GoodUntil, string][]).map(([key, label]) => [label, key]),
) as Record<string, GoodUntil>;

const LEDE =
  "What is on the dial, how likely each one is, and how many are left. The service picks the segment before the wheel moves, so these weights are the odds a visitor actually gets.";

const PREFIX = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{1,4}$/;

export function WheelPrizes({ api, token, activationId, onSessionEnded, onSetup, role, onEnrol }: WheelPrizesProps): ReactNode {
  const frame = useShow();
  const [show, setShow] = useState<Show>({ phase: "reading" });
  const [attempt, setAttempt] = useState(0);
  const [rows, setRows] = useState<Row[]>([]);
  const [weighted, setWeighted] = useState(true);
  const [prefix, setPrefix] = useState("W");
  const [goodUntil, setGoodUntil] = useState<GoodUntil>("show_closes");
  const [emailPrize, setEmailPrize] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ readonly kind: "good" | "bad"; readonly detail: string } | null>(null);

  const take = (desk: PrizeDesk) => {
    setRows(rowsOf(desk));
    // A show that has never saved prizes is opened with them on: the page is
    // where somebody comes to turn them on.
    setWeighted(desk.configured ? desk.weighted : true);
    setPrefix(desk.claimPrefix);
    setGoodUntil(desk.goodUntil);
    setEmailPrize(desk.emailPrize);
  };

  useEffect(() => {
    if (frame.status !== "ready") return;
    let cancelled = false;
    setShow({ phase: "reading" });
    setNote(null);
    void (async () => {
      const result = await readPrizes(api, token, activationId);
      if (cancelled) return;
      if (!result.ok) {
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        setShow({ phase: "failed", failure: result });
        return;
      }
      take(result.value);
      setShow({ phase: "ready", desk: result.value });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, frame.status, attempt]);

  const parsed = useMemo(() => rows.map(parseRow), [rows]);
  const odds = useMemo(() => {
    const live = parsed.map((row) => (row.ok && row.weight > 0 && available(row) ? row.weight : 0));
    const total = live.reduce((sum, w) => sum + w, 0);
    return { total, chance: live.map((w) => (total > 0 ? w / total : 0)) };
  }, [parsed]);

  if (frame.status === "failed") {
    return (
      <Refusal
        onRetry={frame.reload}
        what="Could not load the wheel's prizes"
        failure={frame.failure}
        role={role}
        onEnrol={onEnrol}
        notCounted="No prizes were read, and none were saved, because the activation did not load."
      />
    );
  }
  if (show.phase === "reading") {
    return (
      <div className="stack">
        <PageHead title="Prizes" lede="Reading the dial…" uuid={activationId} />
        <Loading rows={4} cols={6} />
      </div>
    );
  }
  if (show.phase === "failed") {
    return (
      <Refusal
        onRetry={() => setAttempt((n) => n + 1)}
        what="Could not load the wheel's prizes"
        failure={show.failure}
        role={role}
        onEnrol={onEnrol}
        notCounted="No prizes were read, and none were saved, because the service did not answer this page."
      />
    );
  }

  const desk = show.desk;
  const problems = parsed.flatMap((row) => (row.ok ? [] : [row.problem]));
  const prefixOk = PREFIX.test(prefix);
  const canSave = !busy && problems.length === 0 && prefixOk && (!weighted || odds.total > 0);
  const out = parsed.filter((row) => row.ok && row.kind === "prize" && row.stock !== null && row.stock - row.given <= 0);
  const fresh = rows.filter((row) => row.fresh);
  const prizes = parsed.filter((row) => row.kind === "prize");
  const unlimited = prizes.some((row) => row.ok && row.stock === null);
  const left = prizes.reduce((sum, row) => sum + (row.ok && row.stock !== null ? Math.max(0, row.stock - row.given) : 0), 0);
  const winChance = parsed.reduce((sum, row, at) => sum + (row.kind === "prize" ? (odds.chance[at] ?? 0) : 0), 0);

  const onSave = async () => {
    if (!canSave) return;
    setBusy(true);
    setNote(null);
    const result = await savePrizes(api, token, {
      activationId,
      weighted,
      claimPrefix: prefix,
      goodUntil,
      emailPrize,
      segments: parsed.map((row) => ({
        sliceId: row.sliceId,
        kind: row.kind,
        weight: row.ok ? row.weight : 0,
        stock: row.ok && row.kind === "prize" ? row.stock : null,
      })),
    });
    setBusy(false);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    take(result.value);
    setShow({ phase: "ready", desk: result.value });
    setNote({ kind: "good", detail: "Saved. Weights and stock take effect on the next spin. Nothing already won is changed." });
  };

  const patch = (sliceId: string, change: Partial<Row>) =>
    setRows((list) => list.map((row) => (row.sliceId === sliceId ? { ...row, ...change } : row)));

  return (
    <div className="stack">
      <PageHead title="Prizes" lede={LEDE} uuid={activationId} />

      {out.length > 0 ? (
        <NoticeBox kind="amber" title={out.length === 1 ? "1 prize has run out" : `${out.length} prizes have run out`}>
          <p>
            {out.map((row) => row.label || row.sliceId).join(", ")} {out.length === 1 ? "is" : "are"} still on the dial with
            no stock. The service never lands on a prize that has run out, so its weight drops out and every other chance
            on this page goes up. Add stock, or make it a blank.
          </p>
        </NoticeBox>
      ) : null}
      {fresh.length > 0 && desk.configured ? (
        <NoticeBox kind="amber" title="Slices added on Setup have no weight yet">
          <p>
            {fresh.map((row) => row.label || row.sliceId).join(", ")} {fresh.length === 1 ? "was" : "were"} added since the
            prizes were saved. Until this page is saved with a weight for {fresh.length === 1 ? "it" : "them"}, the
            weighted wheel never lands there.
          </p>
        </NoticeBox>
      ) : null}

      <section>
        <SecLabel info="A blank is a segment like any other and carries a weight. The wheel is not rigged to always win, and it does not pretend a run-out prize is still winnable.">
          The odds as they stand
        </SecLabel>
        <div className="figs figs-2">
          <div className="fig">
            <span className="fig-l">Segments</span>
            <span className="fig-v">{rows.length}</span>
            <span className="fig-s">between 4 and 12</span>
          </div>
          <div className="fig">
            <span className="fig-l">Chance of a prize</span>
            <span className="fig-v">{weighted ? `${percent(winChance)}%` : "—"}</span>
            <span className="fig-s">{weighted ? "counting stock" : "equal odds are on"}</span>
          </div>
          <div className="fig">
            <span className="fig-l">Prizes on the dial</span>
            <span className="fig-v">{prizes.length}</span>
            <span className="fig-s">
              {rows.length - prizes.length} {rows.length - prizes.length === 1 ? "blank" : "blanks"}
            </span>
          </div>
          <div className="fig">
            <span className="fig-l">Left to give</span>
            <span className="fig-v">{unlimited ? "Unlimited" : left}</span>
            <span className="fig-s">{unlimited ? `${left} counted, and at least one prize has no limit` : "across every prize"}</span>
          </div>
        </div>
      </section>

      <section>
        <SecLabel info="Off, the wheel is its slices at equal odds, and a prize is told by its label, as it was before prizes. The weights and stock below are kept either way.">
          How the spin picks
        </SecLabel>
        <div className="panel panel-stack">
          <Setting
            label="Pick by weight"
            desc="On, each spin lands by the weights below and never on a prize that has run out. Off, every slice is equally likely."
          >
            <Switch on={weighted} onFlip={() => setWeighted(!weighted)} label={weighted ? "On" : "Off"} />
          </Setting>
        </div>
      </section>

      <section>
        <SecLabel info="The slices and their labels are set on Setup. Here each one is a prize or a blank, with a weight, and a prize can have stock.">
          The dial
        </SecLabel>
        <div className="tablewrap">
          <div className="tablescroll">
            <table className="wide">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Kind</th>
                  <th>Weight</th>
                  <th>Chance</th>
                  <th>Stock</th>
                  <th>Given</th>
                  <th>Left</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, at) => {
                  const read = parsed[at];
                  const stock = read?.ok ? read.stock : null;
                  return (
                    <tr key={row.sliceId}>
                      <td data-label="Segment">
                        {row.label === "" ? <span className="quiet">Slice {at + 1}, no label</span> : row.label}
                        {row.fresh && desk.configured ? <span className="tag tag-warn"> new on Setup</span> : null}
                      </td>
                      <td data-label="Kind">
                        <select
                          className="input"
                          aria-label={`Kind of ${row.label || `slice ${at + 1}`}`}
                          value={row.kind}
                          disabled={busy}
                          onChange={(event) =>
                            patch(row.sliceId, {
                              kind: event.target.value === "prize" ? "prize" : "blank",
                              ...(event.target.value === "blank" ? { stock: "" } : {}),
                            })
                          }
                        >
                          <option value="prize">Prize</option>
                          <option value="blank">Blank</option>
                        </select>
                      </td>
                      <td data-label="Weight">
                        <input
                          className="input num"
                          style={{ width: 72 }}
                          inputMode="numeric"
                          aria-label={`Weight of ${row.label || `slice ${at + 1}`}`}
                          value={row.weight}
                          disabled={busy}
                          onChange={(event) => patch(row.sliceId, { weight: event.target.value.replace(/\D/g, "") })}
                        />
                      </td>
                      <td data-label="Chance" className="num">
                        {weighted ? `${percent(odds.chance[at] ?? 0)}%` : `${percent(1 / Math.max(1, rows.length))}%`}
                      </td>
                      <td data-label="Stock">
                        {row.kind === "prize" ? (
                          <input
                            className="input num"
                            style={{ width: 88 }}
                            inputMode="numeric"
                            placeholder="Unlimited"
                            aria-label={`Stock of ${row.label || `slice ${at + 1}`}`}
                            value={row.stock}
                            disabled={busy}
                            onChange={(event) => patch(row.sliceId, { stock: event.target.value.replace(/\D/g, "") })}
                          />
                        ) : (
                          <span className="quiet">n/a</span>
                        )}
                      </td>
                      <td data-label="Given" className="num">
                        {row.given}
                      </td>
                      <td data-label="Left" className="num">
                        {row.kind !== "prize" ? (
                          <span className="quiet">n/a</span>
                        ) : stock === null ? (
                          "Unlimited"
                        ) : stock - row.given <= 0 ? (
                          <span className="tag tag-warn">none left</span>
                        ) : (
                          stock - row.given
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="acts" style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-quiet" onClick={onSetup}>
            Change the slices on Setup
          </button>
          <span className="help">Stock is how many there are in all; what is left is stock less what has been given.</span>
        </div>
        {problems.length > 0 ? (
          <p className="warn" role="status">
            {problems[0]}
          </p>
        ) : null}
      </section>

      <section>
        <SecLabel>The claim code</SecLabel>
        <div className="panel panel-stack">
          <Setting label="Prefix" desc="Five characters after it, from an alphabet with no O, I or 1: a code is read aloud across a stand.">
            <input
              className="input mono"
              style={{ width: 120 }}
              maxLength={4}
              aria-label="Claim code prefix"
              aria-invalid={!prefixOk}
              value={prefix}
              disabled={busy}
              onChange={(event) => setPrefix(event.target.value.toUpperCase())}
            />
          </Setting>
          <Setting
            label="Good until"
            desc={
              goodUntil === "show_closes"
                ? desk.closesOn === null
                  ? "This show has no closing date on Show details, so its codes do not expire until it has one."
                  : `The end of ${desk.closesOn}, the show's closing day, in its own time zone.`
                : "From the moment of the spin. A code that outlives the show is a code somebody turns up with in October."
            }
          >
            <Seg2
              label="Good until"
              value={GOOD_UNTIL_LABEL[goodUntil]}
              options={Object.values(GOOD_UNTIL_LABEL)}
              onPick={(label) => setGoodUntil(GOOD_UNTIL_OF[label] ?? "show_closes")}
            />
          </Setting>
          <Setting label="Email the prize" desc="A copy of the result and its code goes to the address on the entry. Staff mark it collected on Results.">
            <Switch on={emailPrize} onFlip={() => setEmailPrize(!emailPrize)} label={emailPrize ? "On" : "Off"} />
          </Setting>
        </div>
      </section>

      {note === null ? null : (
        <NoticeBox kind={note.kind} title={note.kind === "good" ? "Prizes saved" : "Prizes not saved"} alert={note.kind === "bad"}>
          <p>{note.detail}</p>
        </NoticeBox>
      )}

      <StickyBar note="Weights and stock take effect on the next spin. Nothing already won is changed.">
        <button type="button" className="btn btn-primary" disabled={!canSave} onClick={() => void onSave()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </StickyBar>
    </div>
  );
}

function rowsOf(desk: PrizeDesk): Row[] {
  return desk.segments.map((segment) => ({
    sliceId: segment.sliceId,
    label: segment.label,
    // A slice with no row yet starts as what its label says, with a weight of
    // one, so turning prizes on for the first time is an even dial.
    kind: segment.kind ?? (looksLikeBlank(segment.label) ? "blank" : "prize"),
    weight: String(segment.kind === null && !desk.configured ? 1 : segment.weight),
    stock: segment.stock === null ? "" : String(segment.stock),
    given: segment.given,
    fresh: segment.kind === null,
  }));
}

type Parsed =
  | { readonly ok: true; readonly sliceId: string; readonly label: string; readonly kind: SegmentKind; readonly weight: number; readonly stock: number | null; readonly given: number }
  | { readonly ok: false; readonly sliceId: string; readonly label: string; readonly kind: SegmentKind; readonly given: number; readonly problem: string };

function parseRow(row: Row): Parsed {
  const name = row.label || row.sliceId;
  const weight = row.weight === "" ? Number.NaN : Number(row.weight);
  if (!Number.isInteger(weight) || weight < 0 || weight > 1000) {
    return { ok: false, sliceId: row.sliceId, label: row.label, kind: row.kind, given: row.given, problem: `${name}: a weight is a whole number from 0 to 1000.` };
  }
  let stock: number | null = null;
  if (row.kind === "prize" && row.stock !== "") {
    stock = Number(row.stock);
    if (!Number.isInteger(stock) || stock > 1_000_000) {
      return { ok: false, sliceId: row.sliceId, label: row.label, kind: row.kind, given: row.given, problem: `${name}: stock is a whole number, or empty for unlimited.` };
    }
    if (stock < row.given) {
      return {
        ok: false,
        sliceId: row.sliceId,
        label: row.label,
        kind: row.kind,
        given: row.given,
        problem: `${name}: ${row.given} have already been won, so there cannot be fewer than ${row.given} in all.`,
      };
    }
  }
  return { ok: true, sliceId: row.sliceId, label: row.label, kind: row.kind, weight, stock, given: row.given };
}

function available(row: Parsed): boolean {
  if (!row.ok) return false;
  return row.kind === "blank" || row.stock === null || row.given < row.stock;
}

function looksLikeBlank(label: string): boolean {
  const name = label.trim();
  return name === "" || name === "-" || name === "—" || /^(not this time|try again|better luck(?: next time)?)$/i.test(name);
}

function percent(share: number): string {
  return String(Math.round(share * 1000) / 10);
}
